import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import LanguageSelect from "../shared/LanguageSelect";
import { useUserStore } from "store";
import { useServerSearch } from "hooks/useServerSearch";
import { getCsrfToken } from "../../utils";
import { calcPriceInclVat, calcPriceExclVat } from "../../utils/vat";
import HelpButton from "../shared/HelpButton";
import VatModeToggle from '../shared/VatModeToggle';
import AutoPickupBackfillDialog from "./AutoPickupBackfillDialog";
import WeekdayHoursEditor from "../shared/WeekdayHoursEditor";
import { DEFAULT_WORK_HOURS, workHoursError } from "../../utils/weekdayHours";

const TIMEZONE_CHOICES = [
  { value: "Europe/Helsinki", label: "Finland (Helsinki)" },
  { value: "Europe/Stockholm", label: "Sweden (Stockholm)" },
  { value: "Europe/Oslo", label: "Norway (Oslo)" },
  { value: "Europe/Copenhagen", label: "Denmark (Copenhagen)" },
  { value: "Europe/Tallinn", label: "Estonia (Tallinn)" },
  { value: "Europe/Riga", label: "Latvia (Riga)" },
  { value: "Europe/Vilnius", label: "Lithuania (Vilnius)" },
  { value: "Europe/Berlin", label: "Germany (Berlin)" },
  { value: "Europe/London", label: "UK (London)" },
  { value: "UTC", label: "UTC" },
];

function OrganizationBasicInfoTab({ t, onDirtyChange }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [organization, setOrganizationRaw] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pientarvikelisaInclVat, setPientarvikelisaInclVat] = useState(false);
  const [pientarvikelisaMinMaxInclVat, setPientarvikelisaMinMaxInclVat] = useState(false);
  // The history action runs against the saved setting, not against an unsaved switch
  const [savedAutoPickup, setSavedAutoPickup] = useState(false);
  const [autoPickupBackfillOpen, setAutoPickupBackfillOpen] = useState(false);
  const {
    items: aiEstimateJobs,
    autocompleteProps: aiEstimateJobsAutocompleteProps,
  } = useServerSearch("/api/jobs/", { loadOnMount: true });
  const logoFileInputRef = useRef(null);
  const colorInputRef = useRef(null);

  useEffect(() => {
    if (currentOrganization?.organization_id) {
      fetchOrganization();
    }
  }, [currentOrganization?.organization_id]);

  // Wrapper: any user-driven state change marks the form dirty
  const setOrganization = useCallback((updater) => {
    setOrganizationRaw(updater);
    setIsDirty(true);
  }, []);

  // Native change event on color input fires only when picker closes (not during drag)
  useEffect(() => {
    const el = colorInputRef.current;
    if (!el) return;
    const handler = (e) => {
      setOrganization((prev) => ({ ...prev, logo_bg_color: e.target.value }));
    };
    el.addEventListener("change", handler);
    return () => el.removeEventListener("change", handler);
  }, [organization?.logo_url]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/organizations/${currentOrganization.organization_id}/`,
        {
          headers: { "X-Organization-Id": currentOrganization.organization_id },
        }
      );
      const data = response.data;
      const minMaxWithVat = data.pientarvikelisa_min_max_entered_with_vat || false;
      // If user saved with VAT, show the incl_vat values in the fields
      if (minMaxWithVat) {
        data.pientarvikelisa_percentage_min = data.pientarvikelisa_percentage_min_incl_vat ?? data.pientarvikelisa_percentage_min;
        data.pientarvikelisa_percentage_max = data.pientarvikelisa_percentage_max_incl_vat ?? data.pientarvikelisa_percentage_max;
      }
      setOrganizationRaw(data);
      setIsDirty(false);
      setSavedAutoPickup(Boolean(data.auto_pickup_on_payment));
      setPientarvikelisaInclVat(false); // Reset toggle - stored value is always excl. VAT
      setPientarvikelisaMinMaxInclVat(minMaxWithVat);
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      setSnackbar({
        open: true,
        message: t("Failed to load organization data"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setOrganization((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const hasChanges = isDirty;

  const handleSave = async () => {
    const workHoursProblem = workHoursError(organization.work_hours);
    if (workHoursProblem) {
      setSnackbar({ open: true, message: t(workHoursProblem), severity: "error" });
      return;
    }
    try {
      setSaving(true);
      await axios.patch(
        `/api/organizations/${organization.id}/`,
        {
          name: organization.name,
          business_id: organization.business_id,
          timezone: organization.timezone,
          work_hours: organization.work_hours,
          auto_schedule_on_start: organization.auto_schedule_on_start,
          separate_quotes_list_enabled: organization.separate_quotes_list_enabled,
          auto_pickup_on_payment: organization.auto_pickup_on_payment,
          require_odometer_on_completion: organization.require_odometer_on_completion,
          require_mfa: organization.require_mfa,
          default_parts_margin: organization.default_parts_margin || 0,
          default_labor_cost_per_hour: organization.default_labor_cost_per_hour,
          default_price_includes_vat: organization.default_price_includes_vat,
          pientarvikelisa_mode: organization.pientarvikelisa_mode || "disabled",
          pientarvikelisa_value: (() => {
            // If user entered value incl. VAT in fixed mode, convert to excl. VAT for storage
            if (organization.pientarvikelisa_mode === "fixed" && pientarvikelisaInclVat) {
              return (parseFloat(organization.pientarvikelisa_value || 0) / 1.255).toFixed(2);
            }
            return organization.pientarvikelisa_value || 0;
          })(),
          pientarvikelisa_auto_apply: organization.pientarvikelisa_auto_apply,
          pientarvikelisa_percentage_min: pientarvikelisaMinMaxInclVat && organization.pientarvikelisa_percentage_min
            ? calcPriceExclVat(organization.pientarvikelisa_percentage_min)
            : organization.pientarvikelisa_percentage_min,
          pientarvikelisa_percentage_min_incl_vat: pientarvikelisaMinMaxInclVat
            ? organization.pientarvikelisa_percentage_min
            : organization.pientarvikelisa_percentage_min
              ? calcPriceInclVat(organization.pientarvikelisa_percentage_min)
              : null,
          pientarvikelisa_percentage_max: pientarvikelisaMinMaxInclVat && organization.pientarvikelisa_percentage_max
            ? calcPriceExclVat(organization.pientarvikelisa_percentage_max)
            : organization.pientarvikelisa_percentage_max,
          pientarvikelisa_percentage_max_incl_vat: pientarvikelisaMinMaxInclVat
            ? organization.pientarvikelisa_percentage_max
            : organization.pientarvikelisa_percentage_max
              ? calcPriceInclVat(organization.pientarvikelisa_percentage_max)
              : null,
          pientarvikelisa_min_max_entered_with_vat: pientarvikelisaMinMaxInclVat,
          ai_estimate_default_job: organization.ai_estimate_default_job || null,
          ai_estimate_multiplier: organization.ai_estimate_multiplier || null,
          default_note_type_customer: organization.default_note_type_customer,
          default_note_show_on_invoice: organization.default_note_show_on_invoice,
          default_show_description_to_customer: organization.default_show_description_to_customer,
          quick_add_fields: organization.quick_add_fields || [],
          phone: organization.phone,
          contact_email: organization.contact_email,
          address_street: organization.address_street,
          address_postal_code: organization.address_postal_code,
          address_city: organization.address_city,
          address_country: organization.address_country,
          default_language: organization.default_language || "fi",
          logo_bg_color: organization.logo_bg_color || "",
        },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      // Refresh user store so nav menu and feature gates update immediately
      await fetchUser();
      // Refresh org data to get saved (excl. VAT) values and reset toggle
      await fetchOrganization();
      setSnackbar({
        open: true,
        message: t("Organization updated successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update organization:", error);
      const message =
        error.response?.data?.detail ||
        error.response?.data?.work_hours?.[0] ||
        t("Failed to update organization");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Report dirty state to parent (MainSettingsView tab-switch guard)
  // Use ref so the callback always calls the latest handleSave (avoids stale closure)
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(hasChanges, () => handleSaveRef.current());
  }, [hasChanges, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: t("settings.organization.logo_invalid_type"),
        severity: "error",
      });
      return;
    }
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: t("settings.organization.logo_too_large"),
        severity: "error",
      });
      return;
    }

    const formData = new FormData();
    formData.append("logo", file);

    try {
      setUploadingLogo(true);
      const response = await axios.post(
        `/api/organizations/${organization.id}/upload-logo/`,
        formData,
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setOrganization(response.data);
      setSnackbar({
        open: true,
        message: t("settings.organization.logo_uploaded"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to upload logo:", error);
      const message =
        error.response?.data?.error || t("settings.organization.logo_upload_failed");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setUploadingLogo(false);
      // Reset file input so same file can be re-selected
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = "";
      }
    }
  };

  const handleLogoDelete = async () => {
    try {
      setUploadingLogo(true);
      const response = await axios.post(
        `/api/organizations/${organization.id}/delete-logo/`,
        {},
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      setOrganization(response.data);
      setSnackbar({
        open: true,
        message: t("settings.organization.logo_deleted"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete logo:", error);
      setSnackbar({
        open: true,
        message: t("settings.organization.logo_delete_failed"),
        severity: "error",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return (
      <Alert severity="error">{t("Failed to load organization data")}</Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Logo */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
            {t("settings.organization.logo")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            {organization.logo_url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  component="img"
                  src={organization.logo_url}
                  alt={organization.name}
                  data-testid="organization-logo-preview"
                  sx={{
                    maxHeight: 60,
                    maxWidth: 200,
                    objectFit: "contain",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 0.5,
                  }}
                />
                <IconButton
                  onClick={handleLogoDelete}
                  disabled={uploadingLogo}
                  color="error"
                  size="small"
                  title={t("settings.organization.delete_logo")}
                  data-testid="delete-logo-button"
                >
                  {uploadingLogo ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("settings.organization.no_logo")}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={uploadingLogo ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={() => logoFileInputRef.current?.click()}
              disabled={uploadingLogo}
              data-testid="upload-logo-button"
            >
              {t("settings.organization.upload_logo")}
            </Button>
            <input
              ref={logoFileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={handleLogoUpload}
              data-testid="logo-file-input"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t("settings.organization.logo_help")}
          </Typography>
          {organization.logo_url && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("settings.organization.logo_bg_color")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  id="logo-bg-color-swatch"
                  sx={{
                    width: 32,
                    height: 32,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: organization.logo_bg_color || "transparent",
                    backgroundImage: !organization.logo_bg_color
                      ? "linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)"
                      : "none",
                    backgroundSize: "8px 8px",
                    backgroundPosition: "0 0, 4px 4px",
                    overflow: "hidden",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onClick={() => document.getElementById("logo-bg-color-input")?.click()}
                  data-testid="logo-bg-color-swatch"
                >
                  <input
                    ref={colorInputRef}
                    id="logo-bg-color-input"
                    type="color"
                    defaultValue={organization.logo_bg_color || "#ffffff"}
                    onInput={(e) => {
                      // Update swatch visually during drag without React re-renders
                      const swatch = document.getElementById("logo-bg-color-swatch");
                      if (swatch) {
                        swatch.style.backgroundColor = e.target.value;
                        swatch.style.backgroundImage = "none";
                      }
                    }}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                    data-testid="logo-bg-color-input"
                  />
                </Box>
                {organization.logo_bg_color && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setOrganization((prev) => ({ ...prev, logo_bg_color: "" }));
                      if (colorInputRef.current) colorInputRef.current.value = "#ffffff";
                    }}
                    data-testid="logo-bg-color-clear"
                  >
                    {t("settings.organization.logo_bg_transparent")}
                  </Button>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t("settings.organization.logo_bg_color_help")}
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
            {t("Company Information")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="name"
            name="name"
            label={t("Organization Name")}
            value={organization.name || ""}
            onChange={handleChange("name")}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Business ID")}
            value={organization.business_id || ""}
            onChange={handleChange("business_id")}
            size="small"
            helperText={t("Finnish Y-tunnus")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Phone")}
            value={organization.phone || ""}
            onChange={handleChange("phone")}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Contact Email")}
            value={organization.contact_email || ""}
            onChange={handleChange("contact_email")}
            size="small"
            type="email"
            helperText={t("contactEmailHelperText")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Street Address")}
            value={organization.address_street || ""}
            onChange={handleChange("address_street")}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label={t("Postal Code")}
            value={organization.address_postal_code || ""}
            onChange={handleChange("address_postal_code")}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label={t("City")}
            value={organization.address_city || ""}
            onChange={handleChange("address_city")}
            size="small"
          />
        </Grid>

        {/* Default Language */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("settings.organization.defaultLanguage", "Default Language")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <LanguageSelect
            fullWidth
            size="small"
            value={organization.default_language || "fi"}
            onChange={handleChange("default_language")}
            label={t("settings.organization.defaultLanguage", "Default Language")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("settings.organization.defaultLanguageHelp", "Sets the default language for communications (emails, SMS, invitations) and for new team members.")}
          </Typography>
        </Grid>

        {/* Work Schedule */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Work Schedule")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Timezone")}</InputLabel>
            <Select
              value={organization.timezone || "Europe/Helsinki"}
              onChange={handleChange("timezone")}
              label={t("Timezone")}
            >
              {TIMEZONE_CHOICES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("workHours.organizationHelp")}
          </Typography>
          <WeekdayHoursEditor
            value={organization.work_hours || DEFAULT_WORK_HOURS}
            onChange={(workHours) => setOrganization((prev) => ({ ...prev, work_hours: workHours }))}
            testIdPrefix="org-work-hours"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.auto_schedule_on_start ?? true}
                onChange={handleChange("auto_schedule_on_start")}
              />
            }
            label={t("autoSchedule.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("autoSchedule.featureToggleHelp")}
          </Typography>
        </Grid>

        {/* Pricing */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Pricing")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <TextField
              fullWidth
              label={`${t("Default Parts Margin")} (${t("markup_short")})`}
              type="number"
              value={organization.default_parts_margin ?? ""}
              onChange={handleChange("default_parts_margin")}
              size="small"
              InputProps={{
                endAdornment: <Typography color="text.secondary">%</Typography>,
              }}
              helperText={t("Percentage added to parts purchase price when selling")}
            />
            <HelpButton
              title={t("Margin vs. markup")}
              text={t("margin_setting_help")}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Default Labor Cost Per Hour (excl. VAT)")}
            type="number"
            value={organization.default_labor_cost_per_hour ?? ""}
            onChange={handleChange("default_labor_cost_per_hour")}
            size="small"
            InputProps={{
              endAdornment: <Typography color="text.secondary">€/h</Typography>,
            }}
            helperText={t("laborCostHelperText")}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.default_price_includes_vat || false}
                onChange={handleChange("default_price_includes_vat")}
              />
            }
            label={t("Default VAT-inclusive price input")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("When creating new items, default to entering prices with VAT included")}
          </Typography>
        </Grid>

        {/* Pientarvikelisä */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Small supplies surcharge")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Small supplies surcharge")}</InputLabel>
            <Select
              value={organization.pientarvikelisa_mode || "disabled"}
              onChange={handleChange("pientarvikelisa_mode")}
              label={t("Small supplies surcharge")}
            >
              <MenuItem value="disabled">{t("pientarvikelisa_disabled")}</MenuItem>
              <MenuItem value="fixed">{t("pientarvikelisa_fixed")}</MenuItem>
              <MenuItem value="percentage">{t("pientarvikelisa_percentage")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {organization.pientarvikelisa_mode && organization.pientarvikelisa_mode !== "disabled" && (
          <>
            <Grid item xs={12} md={organization.pientarvikelisa_mode === "fixed" ? 8 : 4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth
                  label={organization.pientarvikelisa_mode === "fixed"
                    ? (pientarvikelisaInclVat ? t("pientarvikelisa_value_fixed_incl_vat") : t("pientarvikelisa_value_fixed"))
                    : t("pientarvikelisa_value_percentage")}
                  type="number"
                  value={organization.pientarvikelisa_value ?? ""}
                  onChange={handleChange("pientarvikelisa_value")}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <Typography color="text.secondary">
                        {organization.pientarvikelisa_mode === "fixed" ? "€" : "%"}
                      </Typography>
                    ),
                  }}
                />
                {organization.pientarvikelisa_mode === "fixed" && (
                  <VatModeToggle
                    value={pientarvikelisaInclVat ? "incl" : "excl"}
                    onChange={(e, val) => {
                      if (!val) return;
                      const currentValue = parseFloat(organization.pientarvikelisa_value) || 0;
                      if (val === "incl" && !pientarvikelisaInclVat) {
                        // Converting excl → incl: multiply by 1.255
                        const inclValue = (currentValue * 1.255).toFixed(2);
                        setOrganization((prev) => ({ ...prev, pientarvikelisa_value: inclValue }));
                      } else if (val === "excl" && pientarvikelisaInclVat) {
                        // Converting incl → excl: divide by 1.255
                        const exclValue = (currentValue / 1.255).toFixed(2);
                        setOrganization((prev) => ({ ...prev, pientarvikelisa_value: exclValue }));
                      }
                      setPientarvikelisaInclVat(val === "incl");
                    }}
                    sx={{ flexShrink: 0 }}
                    help={false}
                  />
                )}
              </Box>
            </Grid>

            {organization.pientarvikelisa_mode === "percentage" && (
              <>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      label={pientarvikelisaMinMaxInclVat ? t("pientarvikelisa_percentage_min_incl_vat") : t("pientarvikelisa_percentage_min")}
                      type="number"
                      value={organization.pientarvikelisa_percentage_min ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setOrganization((prev) => ({
                          ...prev,
                          pientarvikelisa_percentage_min: val === "" ? null : val,
                        }));
                      }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <Typography color="text.secondary">€</Typography>
                        ),
                      }}
                    />
                    <TextField
                      label={pientarvikelisaMinMaxInclVat ? t("pientarvikelisa_percentage_max_incl_vat") : t("pientarvikelisa_percentage_max")}
                      type="number"
                      value={organization.pientarvikelisa_percentage_max ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setOrganization((prev) => ({
                          ...prev,
                          pientarvikelisa_percentage_max: val === "" ? null : val,
                        }));
                      }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <Typography color="text.secondary">€</Typography>
                        ),
                      }}
                    />
                    <VatModeToggle
                      value={pientarvikelisaMinMaxInclVat ? "incl" : "excl"}
                      onChange={(e, val) => {
                        if (!val) return;
                        const convert = (v) => {
                          const num = parseFloat(v);
                          if (!v || isNaN(num)) return v;
                          return val === "incl" ? calcPriceInclVat(num) : calcPriceExclVat(num);
                        };
                        setOrganization((prev) => ({
                          ...prev,
                          pientarvikelisa_percentage_min: convert(prev.pientarvikelisa_percentage_min),
                          pientarvikelisa_percentage_max: convert(prev.pientarvikelisa_percentage_max),
                        }));
                        setPientarvikelisaMinMaxInclVat(val === "incl");
                      }}
                      sx={{ flexShrink: 0 }}
                      help={false}
                    />
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={organization.pientarvikelisa_auto_apply || false}
                    onChange={handleChange("pientarvikelisa_auto_apply")}
                  />
                }
                label={t("pientarvikelisa_auto_apply")}
              />
            </Grid>
          </>
        )}

        {/* AI Labor Estimates */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("AI labor estimates settings")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={aiEstimateJobs}
            {...aiEstimateJobsAutocompleteProps}
            getOptionLabel={(opt) => opt.name || ""}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            value={aiEstimateJobs.find((j) => j.id === organization.ai_estimate_default_job) || null}
            onChange={(_, val) => {
              setOrganization((prev) => ({ ...prev, ai_estimate_default_job: val?.id || null }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                label={t("AI estimate job")}
                size="small"
                helperText={t("AI estimate job help")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {aiEstimateJobsAutocompleteProps.loading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("AI estimate multiplier")}
            type="number"
            value={organization.ai_estimate_multiplier ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              setOrganization((prev) => ({ ...prev, ai_estimate_multiplier: val === "" ? null : val }));
            }}
            size="small"
            inputProps={{ min: 0.1, max: 10, step: 0.1 }}
            helperText={t("AI estimate multiplier help")}
          />
        </Grid>

        {/* Work Order Defaults */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Work Order Settings")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.separate_quotes_list_enabled ?? false}
                onChange={handleChange("separate_quotes_list_enabled")}
                data-testid="separate-quotes-list-toggle"
              />
            }
            label={t("quotesList.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("quotesList.featureToggleHelp")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.auto_pickup_on_payment ?? false}
                onChange={handleChange("auto_pickup_on_payment")}
                data-testid="auto-pickup-toggle"
              />
            }
            label={t("autoPickup.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("autoPickup.featureToggleHelp")}
          </Typography>
          {savedAutoPickup && organization.auto_pickup_on_payment && (
            <Box sx={{ ml: 6, mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAutoPickupBackfillOpen(true)}
                data-testid="auto-pickup-backfill-button"
              >
                {t("autoPickup.backfillButton")}
              </Button>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.require_odometer_on_completion ?? false}
                onChange={handleChange("require_odometer_on_completion")}
                data-testid="require-odometer-toggle"
              />
            }
            label={t("odometerOnCompletion.featureToggle")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("odometerOnCompletion.featureToggleHelp")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.require_mfa ?? false}
                onChange={handleChange("require_mfa")}
                data-testid="require-mfa-toggle"
              />
            }
            label={t("orgSecurity.requireMfa")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("orgSecurity.requireMfaHelp")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.default_note_type_customer || false}
                onChange={handleChange("default_note_type_customer")}
              />
            }
            label={t("Default note type customer")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Default note type customer help")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.default_note_show_on_invoice ?? true}
                onChange={handleChange("default_note_show_on_invoice")}
              />
            }
            label={t("Default note show on invoice")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Default note show on invoice help")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.default_show_description_to_customer || false}
                onChange={handleChange("default_show_description_to_customer")}
              />
            }
            label={t("Default show description to customer")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Default show description to customer help")}
          </Typography>
        </Grid>

        {/* Quick Add Settings */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Quick Add Settings")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={(organization.quick_add_fields || []).includes("purchase_price")}
                onChange={(e) => {
                  const current = organization.quick_add_fields || [];
                  const updated = e.target.checked
                    ? [...current, "purchase_price"]
                    : current.filter((f) => f !== "purchase_price");
                  setOrganization((prev) => ({ ...prev, quick_add_fields: updated }));
                }}
              />
            }
            label={t("Quick add purchase price")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Quick add purchase price help")}
          </Typography>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <AutoPickupBackfillDialog
        open={autoPickupBackfillOpen}
        organizationId={organization.id}
        onClose={() => setAutoPickupBackfillOpen(false)}
        onDone={(count) => {
          setAutoPickupBackfillOpen(false);
          setSnackbar({ open: true, message: t("autoPickup.backfillDone", { count }), severity: "success" });
        }}
        onError={(message) => {
          setAutoPickupBackfillOpen(false);
          setSnackbar({ open: true, message, severity: "error" });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(OrganizationBasicInfoTab);
